import { useEffect } from 'react';
import { useAppShellDocument } from '@/lib/app-shell';
import { Outlet, useNavigate } from '@tanstack/react-router';
import { useAuth, isAdmin } from '@/lib/auth';
import { TutorSidebar, TutorBottomNav } from '@/components/tutor-nav';
import { tutorGate, useTutorAccess } from '@/lib/tutor/subscription';
import {
  TutorSubscriptionPanel,
  TutorTrialBanner,
} from '@/features/tutor-subscription/tutor-subscription-panel';

export function TutorLayout() {
  // La page ne défile pas (seul le contenu) : barre du bas stable après le clavier iOS.
  useAppShellDocument();
  const { profile, user, loading, profileLoading } = useAuth();
  const navigate = useNavigate();
  const admin = isAdmin(profile, user?.email);
  const isTutor = profile?.role === 'tutor';
  // Abonnement Prof (migration 0691) : les admins ne paient jamais.
  const access = useTutorAccess(isTutor && !admin ? (user?.id ?? null) : null);
  const gate = tutorGate({
    isTutor,
    isAdmin: admin,
    loading: access.isLoading,
    failed: access.isError,
    access: access.data,
  });

  useEffect(() => {
    if (loading || profileLoading) return;
    if (!profile) {
      navigate({ to: '/onboarding' });
      return;
    }
    if (profile.role !== 'tutor' && !admin) {
      navigate({ to: '/' });
    }
  }, [loading, profileLoading, profile, admin, navigate]);

  if (loading || profileLoading || !profile) return null;
  if (profile.role !== 'tutor' && !admin) return null;

  if (gate === 'loading') {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="border-primary-400 h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  // Essai ou abonnement terminé : tout l'espace prof est remplacé par l'offre.
  if (gate === 'paywall' && access.data) {
    return (
      <div className="native:pt-[env(safe-area-inset-top)] h-dvh overflow-y-auto px-4 pb-[calc(2rem+env(safe-area-inset-bottom))]">
        <TutorSubscriptionPanel access={access.data} userId={user?.id ?? null} blocked />
      </div>
    );
  }

  return (
    <div className="flex h-dvh overflow-hidden bg-transparent">
      <TutorSidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <main className="native:pt-[env(safe-area-inset-top)] flex-1 overflow-y-auto pb-24 md:pb-28">
          {access.data && <TutorTrialBanner access={access.data} />}
          <Outlet />
        </main>

        <TutorBottomNav />
      </div>
    </div>
  );
}
